import React from 'react';
import { TrendingUp } from 'lucide-react';
import { formatMoney } from './Markets';

const Figure = ({ label, value, tone }) => (
  <div>
    <dt className="text-xs uppercase tracking-wide text-gray-400">{label}</dt>
    <dd className={`text-lg font-semibold tabular-nums ${tone || 'text-gray-900'}`}>{value}</dd>
  </div>
);

// Money indicators for one title: budget, revenue, profit, return, funding awards.
// `finance` comes from the API; it is null when nothing is known, so render nothing.
export default function ProjectFinance({ finance }) {
  if (!finance) return null;
  const { budget, revenue, profit, roi, awards = [] } = finance;
  const hasFigures = budget != null || revenue != null;

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-4" aria-label="Finance">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-3">
        <TrendingUp className="w-4 h-4 text-videbits-600" /> Finance
      </h3>

      {hasFigures && (
        <>
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Figure label="Budget" value={formatMoney(budget)} />
            <Figure label="Revenue" value={formatMoney(revenue)} />
            <Figure label="Profit" value={formatMoney(profit)}
                    tone={profit != null && profit < 0 ? 'text-red-600' : profit != null ? 'text-green-700' : undefined} />
            <Figure label="Return" value={roi != null ? `${roi}×` : '—'} />
          </dl>
          <p className="text-xs text-gray-400 mt-3">
            Film figures from TMDb, in US dollars, as reported. They are not live box office.
          </p>
        </>
      )}

      {awards.length > 0 && (
        <div className={hasFigures ? 'mt-4 pt-4 border-t border-gray-100' : ''}>
          <h4 className="text-xs uppercase tracking-wide text-gray-400 mb-2">Public funding</h4>
          <ul className="space-y-1 text-sm text-gray-700">
            {awards.map((a, i) => (
              <li key={`${a.funder}-${a.year}-${i}`}>
                <span className="font-medium">{a.funder || 'Funder not named'}</span>
                {[a.program, a.stage, a.year].filter(Boolean).map((part) => ` · ${part}`).join('')}
                {a.source_url && (
                  <>
                    {' '}
                    <a href={a.source_url} target="_blank" rel="noopener noreferrer"
                       className="text-xs text-videbits-700 hover:underline">source</a>
                  </>
                )}
              </li>
            ))}
          </ul>
          <p className="text-xs text-gray-400 mt-2">Amount not published by the funder.</p>
        </div>
      )}
    </section>
  );
}
